<!-- \src\components\UtilizationPricingSettings.vue -->
<template>
  <div class="utilization-pricing-settings">
    <div v-if="loading" class="loading-spinner">Loading...</div>
    <div v-else>
      <div v-if="isAllWeek">
        <div>
          <table class="table table-striped">
            <thead>
              <tr>
                <th>Utilization %</th>
                <th>Price</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(range, index) in utilizationDay?.timeWindows[0]
                  .utilizationRanges"
                :key="range.id"
              >
                <td>
                  <div class="input-group">
                    <input
                      v-model.number="range.low"
                      type="number"
                      min="0"
                      max="100"
                      :class="{
                        'form-control': true,
                        'form-control-error': invalidRange(true, range, index, utilizationDay!),
                      }"
                      @blur="checkRange(true, range, index, utilizationDay!)"
                    />
                    <span class="input-group-text">-</span>
                    <input
                      v-model.number="range.high"
                      type="number"
                      min="0"
                      max="100"
                      :class="{
                        'form-control': true,
                        'form-control-error': invalidRange(false, range, index, utilizationDay!),
                      }"
                      @blur="checkRange(false, range, index, utilizationDay!)"
                    />
                  </div>
                </td>
                <td>
                  <div class="price-unit-container">
                    <PriceUnitComponent
                      class="price-unit"
                      :value="getPriceValue(range)"
                      :isPercent="range.usePercent"
                      @update:value="updatePriceValue(range, $event)"
                      @update:isPercent="updateUsePercent(range, $event)"
                    ></PriceUnitComponent>
                    <small class="text-muted" v-if="range.usePercent">
                      ({{
                        `${Math.abs(
                          calculatePercentageOff(range.pricePercentage)
                        )} % ${
                          calculatePercentageOff(range.pricePercentage) >= 0
                            ? 'off'
                            : 'increase'
                        } `
                      }})
                    </small>
                    <small class="text-muted" v-else> (fixed price) </small>
                  </div>
                </td>
                <td>
                  <button
                    @click="deleteRange(utilizationDay!, index)"
                    class="btn btn-danger btn-sm"
                  >
                    <i class="fa fa-trash-o"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
          <div class="button-group">
            <button
              type="button"
              @click.prevent="addRange(utilizationDay!)"
              class="btn btn-primary"
            >
              <i class="fa fa-plus"></i> Add Row
            </button>
          </div>
        </div>
      </div>
      <div v-else>
        <div v-if="!isAllDay" class="day-tabs">
          <button
            v-for="day in daysOfWeek"
            :key="day.value"
            :class="{ active: activeDay === day.value }"
            @click="
              {
                handleActiveDayChange(day.value);
              }
            "
            class="btn btn-secondary"
          >
            {{ day.label }}
          </button>
        </div>
        <div
          v-for="dailyUtilization in utilizationData"
          :key="dailyUtilization.dayOfWeek"
        >
          <div v-if="isAllDay || dailyUtilization.dayOfWeek === activeDay">
            <table class="table table-striped">
              <thead>
                <tr>
                  <th>Utilization %</th>
                  <th>Price</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(range, index) in dailyUtilization.timeWindows[0]
                    .utilizationRanges"
                  :key="range.id"
                >
                  <td>
                    <div class="input-group">
                      <input
                        v-model.number="range.low"
                        type="number"
                        min="0"
                        max="100"
                        :class="{
                          'form-control': true,
                          'form-control-error': invalidRange(true, range, index, utilizationDay!),
                         }"
                        @blur="checkRange(true, range, index, utilizationDay!)"
                      />
                      <span class="input-group-text">-</span>
                      <input
                        v-model.number="range.high"
                        type="number"
                        min="0"
                        max="100"
                        :class="{
                          'form-control': true,
                          'form-control-error': invalidRange(false, range, index, utilizationDay!),
                        }"
                        @blur="checkRange(false, range, index, utilizationDay!)"
                      />
                    </div>
                  </td>
                  <td>
                    <div class="price-unit-container">
                      <PriceUnitComponent
                        class="price-unit"
                        :value="getPriceValue(range)"
                        :isPercent="range.usePercent"
                        @update:value="updatePriceValue(range, $event)"
                        @update:isPercent="updateUsePercent(range, $event)"
                      ></PriceUnitComponent>
                      <small class="text-muted" v-if="range.usePercent">
                        ({{
                          `${Math.abs(
                            calculatePercentageOff(range.pricePercentage)
                          )} % ${
                            calculatePercentageOff(range.pricePercentage) >= 0
                              ? 'off'
                              : 'increase'
                          } `
                        }})
                      </small>
                      <small class="text-muted" v-else> (fixed price) </small>
                    </div>
                  </td>
                  <td>
                    <button
                      type="button"
                      @click="deleteRange(dailyUtilization, index)"
                      class="btn btn-danger btn-sm"
                    >
                      <i class="fa fa-trash-o"></i>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
            <div class="button-group">
              <button
                v-if="!isDisabled"
                type="button"
                @click.prevent="addRange(dailyUtilization)"
                class="btn btn-primary"
              >
                <i class="fa fa-plus"></i> Add Row
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, watch, PropType, Ref } from 'vue';
import { UtilizationPrice, PriceWindowUtilization } from '@/api';
import { pricingApi } from '@/apiClient';
import PriceUnitComponent from '@/components/PriceUnitComponent.vue';
import { useToast } from 'vue-toastification';
import { AllDay } from '@/utils/dateUtils';

interface DailyUtilization {
  dayOfWeek: number;
  timeWindows: {
    startTime: string;
    endTime: string;
    utilizationRanges: UtilizationPrice[];
  }[];
}

export default defineComponent({
  name: 'UtilizationPricingSettings',
  components: {
    PriceUnitComponent,
  },
  props: {
    initialData: {
      type: Array as PropType<UtilizationPrice[]>,
      default: () => [],
    },
    isAllDay: {
      type: Boolean,
      default: false,
    },
    isAllWeek: {
      type: Boolean,
      default: false,
    },
    priceWindowId: {
      type: Number,
      required: true,
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['save-success', 'save-error'],
  setup(props, { emit }) {
    const loading = ref(true);
    const toast = useToast();
    const utilizationData: Ref<DailyUtilization[]> = ref([]);
    const utilizationDay: Ref<DailyUtilization | undefined> = ref(undefined);
    const activeDay = ref(1);
    const daysOfWeek = [
      { value: 1, label: 'Mon' },
      { value: 2, label: 'Tue' },
      { value: 3, label: 'Wed' },
      { value: 4, label: 'Thu' },
      { value: 5, label: 'Fri' },
      { value: 6, label: 'Sat' },
      { value: 7, label: 'Sun' },
    ];

    const loadData = () => {
      if (props.initialData && props.initialData.length > 0) {
        utilizationData.value = transformInitialData(props.initialData);
      } else {
        utilizationData.value = props.isAllDay
          ? [createEmptyDailyUtilization(0)]
          : daysOfWeek.map((day) => createEmptyDailyUtilization(day.value));
      }

      if (props.initialData && props.initialData.length) {
        utilizationDay.value = getDayUtilization(
          props.isAllWeek ? 0 : 1,
          utilizationData.value
        );
      } else if (props.isAllWeek) {
        utilizationDay.value = getDayUtilization(0, utilizationData.value);
      }

      loading.value = false;
    };

    const calculatePercentageOff = (
      pricePercentage: number | null | undefined
    ): number => {
      if (pricePercentage === null || pricePercentage === undefined) {
        return 0;
      }
      return 100 - pricePercentage;
    };

    const getPriceValue = (range: UtilizationPrice): number => {
      return range.usePercent
        ? range.pricePercentage ?? 100
        : range.priceAmount ?? 0;
    };

    const updatePriceValue = (range: UtilizationPrice, newValue: number) => {
      if (range.usePercent) {
        if (newValue > 100) {
          toast.clear();
          toast.warning(
            'Setting a percentage over 100% will increase the price of the tee time.'
          );
        }
        range.pricePercentage = newValue;
        range.priceAmount = null;
      } else {
        range.priceAmount = newValue;
        range.pricePercentage = null;
      }
    };

    const updateUsePercent = (
      range: UtilizationPrice,
      newIsPercent: boolean
    ) => {
      range.usePercent = newIsPercent;
      if (newIsPercent) {
        // Switched to percentage
        range.pricePercentage = range.pricePercentage ?? 100;
        range.priceAmount = null;
      } else {
        // Switched to fixed amount
        range.priceAmount = range.priceAmount ?? 0;
        range.pricePercentage = 100;
      }
    };

    const createEmptyDailyUtilization = (
      dayOfWeek: number
    ): DailyUtilization => ({
      dayOfWeek,
      timeWindows: [
        {
          startTime: AllDay.startTime,
          endTime: AllDay.endTime,
          utilizationRanges: [
            {
              id: 0,
              priceWindowId: undefined,
              dayOfTheWeek: dayOfWeek,
              startTime: AllDay.startTime,
              endTime: AllDay.endTime,
              low: 0,
              high: 100,
              pricePercentage: 100,
              priceAmount: null,
              timeWindowDescription: null,
              usePercent: true,
            },
          ],
        },
      ],
    });

    const transformInitialData = (
      data: UtilizationPrice[]
    ): DailyUtilization[] => {
      const grouped = data.reduce<Record<number, DailyUtilization>>(
        (acc, item) => {
          if (item.dayOfTheWeek !== undefined) {
            if (!acc[item.dayOfTheWeek]) {
              acc[item.dayOfTheWeek] = {
                dayOfWeek: item.dayOfTheWeek,
                timeWindows: [
                  {
                    startTime: item.startTime || '',
                    endTime: item.endTime || '',
                    utilizationRanges: [],
                  },
                ],
              };
            }
            item.usePercent = item.usePercent ?? true;
            acc[item.dayOfTheWeek].timeWindows[0].utilizationRanges.push(item);
          }
          return acc;
        },
        {}
      );
      return Object.values(grouped);
    };

    const getDayUtilization = (
      dayOfWeek: number,
      du: DailyUtilization[]
    ): DailyUtilization => {
      return (
        du.find((util) => util.dayOfWeek === dayOfWeek) ||
        createEmptyDailyUtilization(dayOfWeek)
      );
    };

    const handleActiveDayChange = (newActiveDay: number) => {
      activeDay.value = newActiveDay;
      utilizationDay.value = getDayUtilization(
        newActiveDay,
        utilizationData.value
      );
    };

    const triggerSave = async (newWindowId = null) => {
      try {
        await saveChanges(newWindowId);
      } catch (error) {
        emit('save-error', error);
        throw error;
      }
    };

    const saveChanges = async (newWindowId = null) => {
      const source =
        props.isAllWeek && utilizationDay.value
          ? [utilizationDay.value]
          : utilizationData.value;

      const priceWindowUtilization: PriceWindowUtilization = {
        priceWindowId: newWindowId ?? props.priceWindowId,
        dailyUtilizations: source.map((daily) => ({
          dayOfWeek: daily.dayOfWeek,
          timeWindows: daily.timeWindows.map((timeWindow) => ({
            startTime: AllDay.startTime,
            endTime: AllDay.endTime,
            timeWindowDescription: 'All Day',
            utilizationRanges: timeWindow.utilizationRanges.map((range) => ({
              low: range.low,
              high: range.high,
              pricePercentage: range.usePercent ? range.pricePercentage : 100,
              priceAmount: range.usePercent ? null : range.priceAmount,
              usePercent: range.usePercent,
              id: range.id,
            })),
          })),
        })),
      };

      try {
        // Call the API to save the utilization data
        const response = await pricingApi.pricingDetailsPost(
          priceWindowUtilization
        );
        console.log(
          'Utilization with low and high data saved successfully:',
          response.data
        );
        // Emit an event if needed
        emit('save-success');
      } catch (error) {
        console.error('Error saving utilization data:', error);
        // Handle the error appropriately, maybe emit an error event
        emit('save-error', error);
      }
    };

    const checkRange = (
      isLowRange: boolean,
      range: UtilizationPrice,
      index: number,
      dailyUtilization: DailyUtilization
    ) => {
      invalidRange(isLowRange, range, index, dailyUtilization, true);
    };

    const invalidRange = (
      isLowRange: boolean,
      range: UtilizationPrice,
      index: number,
      dailyUtilization: DailyUtilization,
      showToast = false
    ) => {
      if (range.low !== undefined && range.high !== undefined) {
        if (exceedsLimits(isLowRange ? range.low : range.high)) {
          if (showToast) {
            toast.warning(
              'Utilization range cannot exceed 100% and not go below 1%.'
            );
          }
          return true;
        }

        if (range.low >= range.high) {
          if (showToast) {
            toast.warning('Low range must be lower than high range.');
          }
          return true;
        }

        if (dailyUtilization !== undefined) {
          if (
            isLowRange &&
            overlapsPreviousRange(range, index, dailyUtilization)
          ) {
            if (showToast) {
              toast.warning('Current range overlaps with previous range.');
            }
            return true;
          }

          if (
            !isLowRange &&
            overlapsNextRange(range, index, dailyUtilization)
          ) {
            if (showToast) {
              toast.warning('Current range overlaps with next range.');
            }
            return true;
          }
        }
      }
    };

    const exceedsLimits = (range: number) => {
      if (range !== undefined) {
        if (range < 0 || range > 100) {
          return true;
        }
        return false;
      }
    };

    const overlapsPreviousRange = (
      range: UtilizationPrice,
      index: number,
      dailyUtilization: DailyUtilization
    ) => {
      if (range.low !== undefined && range.high !== undefined) {
        if (index > 0) {
          const prevRange =
            dailyUtilization.timeWindows[0].utilizationRanges[index - 1];
          if (prevRange.high !== undefined && range.low <= prevRange.high) {
            return true;
          }
        }
      }
      return false;
    };

    const overlapsNextRange = (
      range: UtilizationPrice,
      index: number,
      dailyUtilization: DailyUtilization
    ) => {
      if (range.low !== undefined && range.high !== undefined) {
        if (
          index <
          dailyUtilization.timeWindows[0].utilizationRanges.length - 1
        ) {
          const nextRange =
            dailyUtilization.timeWindows[0].utilizationRanges[index + 1];
          if (nextRange.low !== undefined && nextRange.low <= range.high) {
            return true;
          }
        }
      }
      return false;
    };

    const addRange = (dailyUtilization: DailyUtilization) => {
      const utilizationRanges =
        dailyUtilization.timeWindows[0].utilizationRanges;
      const lastRange = utilizationRanges[utilizationRanges.length - 1];

      const lastHigh = lastRange?.high ?? 0;

      if (lastHigh >= 100) {
        toast.error(
          'Cannot add more ranges. Maximum utilization (100%) has been reached.'
        );
        return;
      }

      const newLow = lastHigh + 1;
      const newHigh = Math.min(newLow + 10, 100); // Default to a 10% range, but cap at 100

      const newRange: UtilizationPrice = {
        id: 0,
        priceWindowId: props.priceWindowId,
        dayOfTheWeek: dailyUtilization.dayOfWeek,
        startTime: AllDay.startTime,
        endTime: AllDay.endTime,
        low: newLow,
        high: newHigh,
        pricePercentage: 100,
        priceAmount: null,
        timeWindowDescription: null,
        usePercent: true,
      };

      utilizationRanges.push(newRange);
    };

    const deleteRange = (dailyUtilization: DailyUtilization, index: number) => {
      if (dailyUtilization.timeWindows[0].utilizationRanges.length > 1) {
        dailyUtilization.timeWindows[0].utilizationRanges.splice(index, 1);
      } else {
        toast.error('You must have at least one utilization range');
      }
    };

    onMounted(() => {
      loadData();
    });

    watch(
      () => props.initialData,
      (newValue) => {
        if (newValue && newValue.length > 0) {
          const dailyUtilization = transformInitialData(newValue);
          utilizationDay.value = getDayUtilization(
            newValue[0].dayOfTheWeek!,
            dailyUtilization
          );
          utilizationData.value = dailyUtilization;
        }
      }
    );

    return {
      loading,
      utilizationData,
      activeDay,
      daysOfWeek,
      saveChanges,
      checkRange,
      invalidRange,
      addRange,
      deleteRange,
      triggerSave,
      getPriceValue,
      updatePriceValue,
      updateUsePercent,
      calculatePercentageOff,
      handleActiveDayChange,
      utilizationDay,
      toast,
    };
  },
});
</script>

<style scoped>
.utilization-pricing-settings {
  font-family: Arial, sans-serif;
  padding: 20px;
  background: #fff;
}

h2 {
  font-size: 24px;
  font-weight: bold;
  margin-bottom: 20px;
}

.loading-spinner {
  text-align: center;
  padding: 20px;
}

.day-tabs {
  display: flex;
  margin-bottom: 1rem;
}

.day-tabs button {
  margin-right: 5px;
}

.day-tabs button.active {
  background-color: #a4c5c7;
  border-color: #a4c5c7;
  color: #3c4f50;
  font-weight: bold;
}

.table {
  margin-bottom: 1rem;
}

.input-group {
  width: auto;
}

.button-group {
  display: flex;
  justify-content: flex-end;
  margin-top: 1rem;
}

.button-group button {
  margin-left: 10px;
}

.form-control:focus {
  border-color: #618183;
  box-shadow: 0 0 0 0.25rem rgba(64, 95, 107, 0.25);
}

.form-control-error {
  border-color: #c82333;
}

.btn-primary {
  background-color: #405f6b;
  border-color: #405f6b;
}

.btn-primary:hover,
.btn-primary:focus {
  background-color: #618183;
  border-color: #618183;
}

.btn-danger {
  background-color: #dc3545;
  border-color: #dc3545;
}

.btn-danger:hover,
.btn-danger:focus {
  background-color: #c82333;
  border-color: #bd2130;
}

.btn-success {
  background-color: #28a745;
  border-color: #28a745;
}

.btn-success:hover,
.btn-success:focus {
  background-color: #218838;
  border-color: #1e7e34;
}

/* New Styles for PriceUnitComponent and <small> */
.price-unit-container {
  display: flex;
  align-items: center;
}

.price-unit {
  max-width: 150px; /* Adjust the width as needed */
  margin-right: 10px; /* Space between the component and the text */
}

.price-unit-container small {
  white-space: nowrap;
}

/* Responsive Adjustments */
@media (max-width: 768px) {
  .price-unit {
    max-width: 100px; /* Adjust for smaller screens */
  }
}
</style>
